/** Navigation rail + top bar. Deliberately dense and tabular rather than
 *  card-based: the job is to make the day's work legible at a glance, not to
 *  replace their mental model of it. */
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type DaySummary, type IncidentCount, type QueueRow } from '@/api/client'
import { useAuth } from '@/lib/auth'
import { ThemePicker } from '@/components/ThemePicker'
import { AwLogo } from './AwLogo'
import mark from '@/assets/taskmanager-mark.png'
import { fmtDate, todayISO } from './ui'
import { BottomTabs } from './BottomTabs'
import { AddTaskDrawer } from './AddTaskDrawer'
import { Toasts } from './Toasts'

/** One rail entry. `owner`/`superuser` hide it; `badge` names a count in `badges`
 *  below rather than being special-cased at the render. */
type NavItem = {
  to: string; label: string; icon: string
  owner?: boolean; superuser?: boolean; badge?: string
  /** Shown to a manager with task entry as well as to owners. */
  manager?: boolean
  /** A key in `me.permissions`. The entry is hidden unless the server says yes.
   *
   *  Reports, Filter & export and Approvals sit behind approver-or-owner endpoints
   *  but had no flag at all, so a responsible person was shown three screens that
   *  answered 403 the moment they were opened. Reading the permission map rather
   *  than inspecting roles here keeps one authority on the answer - the server's. */
  needs?: 'reports' | 'approve'
}

const NAV: NavItem[] = [
  { to: '/today', label: 'Today', icon: 'rows' },
  // Everybody's, with no `needs` and no role flag - the one entry here that is never
  // hidden from anybody.
  { to: '/notices', label: 'Notifications', icon: 'bell', badge: 'notices' },
  { to: '/new', label: "Write the day's list", icon: 'plus', owner: true,
    manager: true },
  { to: '/tasks', label: 'All tasks', icon: 'table' },
  { to: '/approvals', label: 'Approvals', icon: 'stamp', badge: 'approvals',
    needs: 'approve' },
  { to: '/carried', label: 'Carried forward', icon: 'clock' },
  { to: '/day-close', label: 'Day close', icon: 'cal', owner: true },
]
const MANAGE: NavItem[] = [
  { to: '/recurring', label: 'Recurring', icon: 'repeat', owner: true },
  { to: '/reminders', label: 'Reminders', icon: 'bell', owner: true },
  // Superuser, not owner: switching the firm between WhatsApp and Telegram
  // changes how every person is reached, and it is not one owner's to make.
  { to: '/channel', label: 'Message channel', icon: 'chat', superuser: true },
  { to: '/timezone', label: 'Timezone', icon: 'clock', superuser: true },
  { to: '/calendar', label: 'Working calendar', icon: 'cal', owner: true },
  { to: '/master', label: 'Master data', icon: 'users', owner: true },
  { to: '/reports', label: 'Reports', icon: 'chart', needs: 'reports' },
  // "Build a report" was accurate and unfindable. Three separate requests for
  // filters, grouping and export on All tasks turned out to be requests for this
  // screen, which already had all three. Named for what somebody looks for.
  { to: '/explore', label: 'Filter & export', icon: 'table', needs: 'reports' },
  { to: '/insights', label: 'Insights', icon: 'chart', needs: 'reports' },
  { to: '/team', label: 'Team', icon: 'users', owner: true },
  // Superuser rather than owner: alerts are sent to one account, and the screen is
  // that account's. The endpoints behind it are owner_only all the same.
  { to: '/system', label: 'System', icon: 'bell', superuser: true,
    badge: 'incidents' },
]

function Icon({ name }: { name: string }) {
  const d: Record<string, string> = {
    rows: 'M3 4h18v4H3zM3 11h18v4H3zM3 18h18v3H3z',
    plus: 'M12 5v14M5 12h14',
    table: 'M3 3h18v18H3zM3 9h18M9 9v12',
    stamp: 'M6 21h12M8 17h8v2H8zM9 17v-3a3 3 0 01-3-3V8a3 3 0 013-3h6a3 3 0 013 3v3a3 3 0 01-3 3v3',
    clock: 'M12 3a9 9 0 100 18 9 9 0 000-18zM12 7v5l3.5 2',
    bell: 'M12 3a6 6 0 00-6 6v4l-2 3h16l-2-3V9a6 6 0 00-6-6zM9 19a3 3 0 006 0',
    chat: 'M4 4h16v11H9l-5 4z',
    cal: 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4',
    users: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 3a4 4 0 100 8 4 4 0 000-8z',
    chart: 'M4 20h16M6 11v7M11 6v12M16 14v4',
    repeat: 'M17 2l4 4-4 4M3 11V9a4 4 0 014-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 01-4 4H3',
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="opacity-75 shrink-0">
      <path d={d[name] ?? d.rows} />
    </svg>
  )
}

export function Shell() {
  const { me, signOut } = useAuth()
  const loc = useLocation()
  const { data: day } = useQuery({
    queryKey: ['day', todayISO()],
    queryFn: () => api.get<DaySummary>(`/days/${todayISO()}`),
    refetchInterval: 60_000,
  })
  // Counts only what is asked of *you*. An Owner's queue is the whole firm's, so the
  // badge used to sit at forty and never clear whatever they did - a number nobody
  // can act on is a number people learn to ignore. The team rows are still one tab
  // away on the screen itself.
  const { data: queue } = useQuery({
    queryKey: ['queue-count'],
    queryFn: async () => (await api.get<QueueRow[]>('/approvals/queue'))
      .filter((r) => r.whose === 'mine'),
    enabled: !!me && (me.is_owner || me.roles.includes('APPROVER')),
    refetchInterval: 60_000,
  })
  // Fifteen seconds, not sixty, and only while the tab is looked at: this one drives
  // the pop-up as well as the badge, and a notification that turns up a minute late is
  // a notification somebody has already been told about by somebody walking over.
  const { data: notices } = useQuery({
    queryKey: ['notices-count'],
    queryFn: () => api.get<{ open: number }>('/notices/count'),
    enabled: !!me,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  })
  const { data: incidents } = useQuery({
    queryKey: ['incident-count'],
    queryFn: () => api.get<IncidentCount>('/ops/incidents/count'),
    enabled: !!me?.is_superuser,
    refetchInterval: 60_000,
  })

  const isOwner = !!me?.is_owner
  const isSuperuser = !!me?.is_superuser
  const mayEnterTasks = !!me?.may_enter_tasks
  // One place that turns a badge name into a number. A second badge should not mean
  // a second special case in `link` below.
  const badges: Record<string, number | undefined> = {
    notices: notices?.open,
    approvals: queue?.length,
    incidents: incidents?.open,
  }
  // Below `lg` the rail is an off-canvas drawer. A 212px shrink-0 sidebar left about
  // 138px of content width on a 390px phone, which is why nothing else on the page
  // could be made to work until this moved.
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => { setNavOpen(false) }, [loc.pathname])
  // The add sheet lives here rather than on a page, because the bottom bar can open
  // it from any of the four screens and only the shell is mounted on all of them.
  // Today keeps its own Add button: that one sits in the day bar beside Send digest,
  // which is desk furniture, and the two are never on screen together.
  const [adding, setAdding] = useState(false)
  useEffect(() => { setAdding(false) }, [loc.pathname])
  // A drawer that stays open behind a widened window would be invisible and still
  // trap focus, so it closes when the layout goes back to two columns.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = () => { if (mq.matches) setNavOpen(false) }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const link = (i: NavItem) => {
    if (i.owner && !isOwner && !(i.manager && mayEnterTasks)) return null
    if (i.superuser && !isSuperuser) return null
    if (i.needs && !me?.permissions?.[i.needs]) return null
    const badge = (i.badge && badges[i.badge]) || null
    return (
      <NavLink key={i.to} to={i.to}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-4 py-1.5 text-[13px] border-l-[2.5px] transition-colors ${
            isActive ? 'bg-surface text-primary font-semibold border-primary'
                     : 'text-ink-2 border-transparent hover:text-ink'}`}>
        <Icon name={i.icon} />
        {i.label}
        {badge ? (
          <span className="ml-auto mono text-[11.5px] rounded-full bg-pend text-white px-1.5">
            {badge}
          </span>
        ) : null}
      </NavLink>
    )
  }

  const manageLinks = MANAGE.map(link)

  const title = [...NAV, ...MANAGE].find((n) => loc.pathname.startsWith(n.to))?.label ?? 'Today'

  return (
    <div className="flex h-screen overflow-hidden">
      {/* The backdrop only exists while the drawer is open, and only below `lg`. */}
      {navOpen ? (
        <div className="fixed inset-0 z-30 bg-ink/30 lg:hidden"
             onClick={() => setNavOpen(false)} aria-hidden />
      ) : null}
      {/* Only ONE transform utility, applied when closed. Toggling between
          `translate-x-0` and `-translate-x-full` does not work: both land in the same
          media query at the same specificity, so Tailwind's canonical source order
          decides which wins rather than the template string - the drawer stayed at
          -212px with the state correctly flipped and the backdrop showing. */}
      <nav className={`w-[212px] shrink-0 bg-rail border-r border-rule flex flex-col
                       pt-4 pb-3
                       max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-40
                       max-lg:shadow-2xl max-lg:transition-transform
                       ${navOpen ? '' : 'max-lg:-translate-x-full'}`}>
        <div className="px-4 pb-4 border-b border-rule mb-3">
          {/* The mark beside the name rather than above it: the rail is 212px and
              has to carry fourteen links, the signed-in user and the credit, so
              height here is the expensive dimension. The name stays live text, which
              is sharper than any image at this size.

              The mark is decorative here - the text names the app - so it is hidden
              from screen readers rather than announced twice. */}
          <div className="flex items-center gap-2.5">
            <img src={mark} alt="" aria-hidden width={30}
                 className="block shrink-0" />
            <b className="text-[14.5px] font-semibold tracking-tight">Task Manager</b>
          </div>
          {/* From package.json via Vite, not typed here. The hardcoded "V1" this
              replaces was read from nothing, so it would have gone on saying V1
              through every release. */}
          <span className="mono block text-[11.5px] text-ink-3 mt-0.5">
            v{__APP_VERSION__}
          </span>
        </div>
        {/* Only the links scroll. The brand above and the identity and credit below
            stay put, so a short window loses a nav item off the bottom of this list
            rather than losing the whole footer - which is what happened when the
            taller logo header pushed the credit past the bottom of the rail. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {NAV.map(link)}
          {/* The heading only where there is something under it. With every entry in
              this group hidden - which is now every responsible person - it was a
              section label sitting alone above the sign-out. */}
          {manageLinks.some(Boolean) && (
            <div className="text-2xs font-semibold uppercase tracking-wider text-ink-3 px-4 pt-3.5 pb-1">
              Manage
            </div>
          )}
          {manageLinks}
        </div>
        {/* Two rows, not one. The five theme swatches and the identity block together
            need more than the rail's 212px: side by side, the name truncated to a
            single letter and "Sign out" wrapped onto two lines. Who you are on top,
            the colours under it. */}
        <div className="shrink-0 px-4 pt-2.5 mt-2.5 border-t border-rule">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-full bg-primary text-primary-on grid
                            place-items-center text-[11px] font-semibold">
              {(me?.name ?? '?').split(' ').filter(Boolean).slice(-2).map((s) => s[0]).join('')}
            </div>
            <div className="min-w-0">
              <b className="block text-xs font-semibold truncate">{me?.name}</b>
              <button onClick={signOut} className="text-[11px] text-ink-3 hover:text-rej">
                Sign out
              </button>
            </div>
          </div>
          <div className="pt-2">
            <ThemePicker />
          </div>
        </div>

        {/* Last down the rail, so it reads: what you can do, who you are, who made it.
            Centred, and that is the point: the wordmark is centred artwork - "A&W"
            sits narrower than "Engineering Works" beneath it - so a left-aligned
            label above it left three different left edges in a column and looked
            like a mistake. One centre line for both. */}
        <div className="shrink-0 px-4 pt-2.5 mt-2.5 border-t border-rule text-ink-3
                        text-center">
          <span className="block text-[10.5px] mb-1">Powered by</span>
          <AwLogo height={28} className="mx-auto" />
        </div>
      </nav>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[57px] shrink-0 bg-surface border-b border-rule flex items-center gap-3.5 px-4 sm:px-5">
          <button type="button" onClick={() => setNavOpen((v) => !v)}
                  aria-label="Menu" aria-expanded={navOpen}
                  className="lg:hidden -ml-1 grid h-10 w-10 shrink-0 place-items-center
                             rounded-md text-ink-2 hover:bg-paper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <h1 className="text-[15px] sm:text-[17px] font-semibold tracking-tight truncate">
            {title}
          </h1>
          {/* The date is the first thing worth losing when the bar gets tight. */}
          <span className="mono text-xs text-ink-3 pl-3.5 border-l border-rule
                           hidden sm:inline">
            {/* Through `fmtDate` so it names the firm's day, not the browser's. It
                read `new Date()` directly, which is the one date on screen with no
                server value behind it - so on a laptop in another zone the header
                could disagree with every list under it. */}
            {fmtDate(todayISO())}
          </span>
          <span className="flex-1" />
          {day?.calendar_warning && (
            <span className="text-[11.5px] text-pend-ink bg-pend-tint rounded px-2 py-1
                             hidden md:inline">
              {day.calendar_warning}
            </span>
          )}
        </header>
        {/* `pb-20` on a phone so the last card clears the tab bar. Without it the
            bar covers the final row of every list, which is where the thing you
            scrolled all the way down to find usually is. */}
        <main className="flex-1 overflow-auto p-3.5 sm:p-5 pb-20 md:pb-5">
          <Outlet />
        </main>
      </div>
      <BottomTabs onAdd={() => setAdding(true)} approvals={queue?.length} />
      {adding && <AddTaskDrawer onClose={() => setAdding(false)} />}
      {/* Here because it is the only component mounted on every screen, and a
          notification that only appears on some of them is not a notification. */}
      <Toasts />
    </div>
  )
}
